import { ParsedGarmentInfo, UserFile } from 'service/user'
import api from './api'
import {
  AIVideoParams,
  LipSyncParams,
  UGCVideoParams,
  ProductCharacterParams,
  BackgroundMakerParams,
  StyleImageParams,
  AIStudioGenerationResponse,
  APIResponse,
  BetaTestFormValues,
} from '../pages/ai-studio/types/aiStudio'
import {
  ImageAPIResponse,
  VideoAPIResponse,
} from '../pages/ai-studio/gallery/types'

export const aiStudioService = {
  //업로드 이미지 비져니
  getUploadImageInfo: async (id: number): Promise<UserFile> => {
    return api.get(`/ai-studio/upload/${id} `)
  },

  // 이미지 분석 재시도
  retryImageAnalysis: async (imageId: number): Promise<UserFile> => {
    return api.post(`/ai-studio/upload/${imageId}/analyze`)
  },
  // AI 영상 생성
  generateAIVideo: async (
    params: AIVideoParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    const formData = new FormData()
    formData.append('image', params.image)
    formData.append('duration', params.duration.toString())

    if (params.style) {
      formData.append('style', params.style)
    }

    if (params.motion_intensity) {
      formData.append('motion_intensity', params.motion_intensity.toString())
    }

    return api.post('/ai-studio/video/ai-video/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // 립씽크 영상 생성
  generateLipSync: async (
    params: LipSyncParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    const formData = new FormData()
    formData.append('character_image', params.character_image)

    if (params.audio_file) {
      formData.append('audio_file', params.audio_file)
    }

    if (params.text_script) {
      formData.append('text_script', params.text_script)
    }

    if (params.voice_type) {
      formData.append('voice_type', params.voice_type)
    }

    return api.post('/ai-studio/video/lip-sync/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // UGC 영상 생성
  generateUGCVideo: async (
    params: UGCVideoParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    return api.post('/ai-studio/video/ugc/generate', params)
  },

  // 상품+캐릭터 이미지 생성
  generateProductCharacter: async (
    params: ProductCharacterParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    const formData = new FormData()
    formData.append('product_image', params.product_image)
    formData.append('character_type', params.character_type)

    if (params.style) {
      formData.append('style', params.style)
    }

    if (params.background) {
      formData.append('background', params.background)
    }

    return api.post('/ai-studio/image/product-character/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // 배경 만들기
  generateBackgroundMaker: async (
    params: BackgroundMakerParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    const formData = new FormData()
    formData.append('target_image', params.target_image)
    formData.append('background_style', params.background_style)

    if (params.remove_background !== undefined) {
      formData.append('remove_background', params.remove_background.toString())
    }

    return api.post('/ai-studio/image/background/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // 스타일 이미지 생성
  generateStyleImage: async (
    params: StyleImageParams,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    const formData = new FormData()

    formData.append('first_image', params.first_image)

    if (params.second_image) {
      formData.append('second_image', params.second_image)
    }

    if (params.description) {
      formData.append('description', params.description)
    }

    if (params.selectedPreset) {
      formData.append('selectedPreset', params.selectedPreset)
    }

    return api.post('/ai-studio/image/style/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // 생성 상태 조회
  getGenerationStatus: async (
    id: string,
  ): Promise<APIResponse<AIStudioGenerationResponse>> => {
    return api.get(`/ai-studio/generation/${id}`)
  },

  // 생성 히스토리 조회
  getGenerationHistory: async (
    type?: string,
    page = 1,
    limit = 20,
  ): Promise<
    APIResponse<{
      items: AIStudioGenerationResponse[]
      total: number
      page: number
      limit: number
    }>
  > => {
    const params: Record<string, any> = { page, limit }
    if (type) params.type = type

    return api.get('/ai-studio/history', { params })
  },

  // 생성 결과 삭제
  deleteGeneration: async (id: string): Promise<APIResponse<boolean>> => {
    return api.delete(`/ai-studio/generation/${id}`)
  },

  // 생성 취소
  cancelGeneration: async (id: string): Promise<APIResponse<boolean>> => {
    return api.post(`/ai-studio/generation/${id}/cancel`)
  },

  // 사용 가능한 스타일 목록 조회
  getAvailableStyles: async (
    type: string,
  ): Promise<
    APIResponse<
      {
        id: string
        name: string
        preview: string
        description?: string
      }[]
    >
  > => {
    return api.get(`/ai-studio/styles/${type}`)
  },

  // 사용 가능한 캐릭터 타입 조회
  getAvailableCharacters: async (): Promise<
    APIResponse<
      {
        id: string
        name: string
        preview: string
        category: string
      }[]
    >
  > => {
    return api.get('/ai-studio/characters')
  },

  // 사용 가능한 음성 타입 조회
  getAvailableVoices: async (): Promise<
    APIResponse<
      {
        id: string
        name: string
        gender: 'male' | 'female'
        language: string
        preview_url?: string
      }[]
    >
  > => {
    return api.get('/ai-studio/voices')
  },

  // google storage file upload
  uploadFile: async (
    file: File,
  ): Promise<{
    fileUrl: string
    fileName: string
    fileSize: number
  }> => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post('/ai-studio/upload/single-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // 아바타 필터 옵션 조회
  getAvatarFilters: async (): Promise<{
    genders: Array<{ value: string; label: string }>
    ethnicities: Array<{ value: string; label: string }>
  }> => {
    return api.get('/ai-studio/avatars/filters')
  },

  // 전체 아바타 조회 (파라미터 무시하고 전체 반환)
  getFilteredAvatars: async (params?: {
    gender?: string[]
    ethnicity?: string[]
  }): Promise<AvatarType[]> => {
    // 파라미터 무시하고 전체 아바타 목록 반환
    return api.get('/ai-studio/avatars/filter')
  },
  // 사용 가능한 캐릭터 타입 조회
  getAvatars: async (): Promise<
    {
      id: number
      name: string
      description: string
      thumbnailUrl: string
      isActive: number
      sortOrder: number
    }[]
  > => {
    return api.get('/ai-studio/avatars?activeOnly=true')
  },

  // 베타 테스트 신청 제출
  submitBetaTestRequest: async (
    params: BetaTestFormValues,
  ): Promise<APIResponse<{ requestId: string }>> => {
    return api.post('/ai-studio/beta/synthesis-request', params)
  },

  // 패션 AI 편집 생성 (임시 API)
  generateFashionEdit: async (params: {
    images: string[]
    prompt: string
    model?: string
  }): Promise<{
    job_id: string
    status: string
    message: string
    created_at: string
    status_url: string
    estimated_time: string
  }> => {
    return api.post(
      'https://asia-northeast3-fast-cut-422707.cloudfunctions.net/ai-studio-proxy',
      {
        images: params.images,
        model: params.model || 'gemini-2.5-flash-image-preview',
        prompt: params.prompt,
      },
      {
        withCredentials: false,
        skipAuth: true, // TypeScript 오류 방지
      } as any, // << 타입 단언
    )
  },

  // 패션 AI 편집 작업 상태 조회 (임시 API)
  getFashionEditStatus: async (
    jobId: string,
  ): Promise<{
    job_id: string
    status: 'queued' | 'processing' | 'completed' | 'failed'
    progress?: number
    message?: string
    result_url?: string
    result_urls?: string[]
    error?: string
    created_at: string
    updated_at?: string
  }> => {
    return api.get(
      `https://asia-northeast3-fast-cut-422707.cloudfunctions.net/ai-studio-status/${jobId}`,
      {
        withCredentials: false,
        skipAuth: true, // TypeScript 오류 방지
      } as any, // << 타입 단언
    )
  },

  // 포토세트 생성 (임시 API)
  generatePhotoSet: async (params: {
    avatar: AvatarType
    clothingImages: string[]
    prompt: string
    model?: string
  }): Promise<{
    job_id: string
    status: string
    message: string
    created_at: string
    status_url: string
    estimated_time: string
  }> => {
    const allImages = [...params.clothingImages, params.avatar.thumbnailUrl]
    const photoSetPrompt = `${params.prompt} 다양한 포즈와 컨셉으로 스타일 샷 세트를 생성해주세요. **텍스트 설명 없이, 수정된 최종 이미지들만** 응답하세요.`

    return api.post(
      'https://asia-northeast3-fast-cut-422707.cloudfunctions.net/ai-studio-proxy',
      {
        images: allImages,
        model: params.model || 'gemini-2.5-flash-image-preview',
        prompt: photoSetPrompt,
      },
      {
        withCredentials: false,
        skipAuth: true,
      } as any,
    )
  },

  // 포토세트 작업 상태 조회 (임시 API)
  getPhotoSetStatus: async (
    jobId: string,
  ): Promise<{
    job_id: string
    status: 'queued' | 'processing' | 'completed' | 'failed'
    progress?: number
    message?: string
    result_url?: string
    result_urls?: string[]
    error?: string
    created_at: string
    updated_at?: string
  }> => {
    return api.get(
      `https://asia-northeast3-fast-cut-422707.cloudfunctions.net/ai-studio-status/${jobId}`,
      {
        withCredentials: false,
        skipAuth: true,
      } as any,
    )
  },

  //
  garmentSynthesisRequestsBySelectGarmentId: async (
    id: number,
    garmentJobId: number,
  ): Promise<APIResponse<GamrentSynthesisStatusResponse>> => {
    return api.post(`/ai-studio/synthesis-requests/${id}/select-garment`, {
      garmentJobId,
    })
  },

  // 의류 정보 업데이트 API
  updateGarmentInfo: async (
    imageId: number,
    params: UpdateGarmentInfoParams,
  ): Promise<APIResponse<UserFile>> => {
    return api.put(
      `/ai-studio/user-uploaded-images/${imageId}/garment-info`,
      params,
    )
  },

  // 착샷 합성 프로세스 api
  garmentSynthesisRequests: async (
    params: GarmentSynthesisRequestParams,
  ): Promise<APIResponse<SynthesisRequestResponse>> => {
    return api.post('/ai-studio/garment-synthesis-requests', params)
  },
  // Synthesis Request 생성 (새로운 API)
  submitSynthesisRequest: async (
    params: SynthesisRequestParams,
  ): Promise<APIResponse<SynthesisRequestResponse>> => {
    return api.post('/ai-studio/synthesis-requests-sample', params)
  },

  // GamrentSynthesis Request 상태 조회 (새로운 API)
  getGarmetSynthesisRequestStatus: async (
    requestId: number,
  ): Promise<GamrentSynthesisStatusResponse> => {
    return api.get(`/ai-studio/garment-synthesis-requests/${requestId}`)
  },

  // Synthesis Request 상태 조회 (새로운 API)
  getSynthesisRequestStatus: async (
    requestId: number,
  ): Promise<SynthesisStatusResponse> => {
    return api.get(`/ai-studio/synthesis-requests/${requestId}/status`)
  },

  // PhotoSet 생성용 Synthesis Request (일반 API)
  submitPhotoSetSynthesisRequest: async (
    params: SynthesisRequestParams,
  ): Promise<APIResponse<SynthesisRequestResponse>> => {
    return api.post('/ai-studio/synthesis-requests', params)
  },

  // 비디오 생성 요청 (새로운 API)
  submitVideoSynthesisRequest: async (
    params: VideoSynthesisRequestParams,
  ): Promise<APIResponse<VideoSynthesisRequestResponse>> => {
    return api.post('/ai-studio/video-synthesis-requests', params)
  },

  // 비디오 생성 상태 조회 (새로운 API)
  getVideoSynthesisRequestStatus: async (
    requestId: number,
  ): Promise<VideoSynthesisRequestResponse> => {
    return api.get(`/ai-studio/video-synthesis-requests/${requestId}`)
  },

  // 갤러리 이미지 조회
  getMyImages: async (
    page: number,
    limit: number,
  ): Promise<ImageAPIResponse> => {
    return api.get('/ai-studio/my-images', {
      params: { page, limit },
    })
  },

  // 갤러리 비디오 조회
  getMyVideos: async (
    page: number,
    limit: number,
  ): Promise<VideoAPIResponse> => {
    return api.get('/ai-studio/my-videos', {
      params: { page, limit },
    })
  },

  //garmentType에 따른 샘플 조회
  getSampleByGarment: async (garmentType: string): Promise<SampleResponse> => {
    return api.get('/ai-studio/sample-garment-images', {
      params: { garmentType },
    })
  },

  //garmentType에 따른 샘플 조회 (페이지네이션 지원)
  getSampleByGarmentWithPagination: async (params: {
    garmentType: string
    page?: number
    limit?: number
  }): Promise<SampleResponse> => {
    return api.get('/ai-studio/sample-garment-images', {
      params: {
        garmentType: params.garmentType,
        page: params.page || 1,
        limit: params.limit || 20,
      },
    })
  },

  // 비디오 할당량 조회
  getVideoQuota: async (): Promise<VideoQuotaResponse> => {
    return api.get('/ai-studio/users/me/video-quota')
  },

  // 합성 작업 삭제
  deleteSynthesisJob: async (jobId: number): Promise<void> => {
    return api.delete(`/ai-studio/synthesis-jobs/${jobId}`)
  },

  // 비디오 합성 요청 삭제
  deleteVideoSynthesisRequest: async (requestId: number): Promise<void> => {
    return api.delete(`/ai-studio/video-synthesis-requests/${requestId}`)
  },

  // 합성 작업 전체 정보 조회 (테스트용)
  getSynthesisJobFull: async (jobId: number): Promise<any> => {
    return api.get(`/ai-studio/synthesis-jobs/${jobId}/full`)
  },

  // garment 정보
  getGarmentSynthesisJobFull: async (id: number): Promise<any> => {
    return api.get(`/ai-studio/garment-synthesis-jobs/${id}`)
  },

  // 자유 합성 요청
  generateFreeSynthesis: async (
    params: FreeSynthesisParams,
  ): Promise<FreeSynthesisResponse> => {
    return api.post('/ai-studio/free-synthesis', params)
  },

  // 자유 합성 목록 조회
  getFreeSynthesisList: async (params: {
    page?: number
    limit?: number
    status?: string
    requestType?: string
  }): Promise<FreeSynthesisListResponse> => {
    return api.get('/ai-studio/free-synthesis', {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        ...(params.status && { status: params.status }),
        ...(params.requestType && { requestType: params.requestType }),
      },
    })
  },

  // 자유 합성 상태 조회
  getFreeSynthesisStatus: async (id: number): Promise<FreeSynthesisRequest> => {
    return api.get(`/ai-studio/free-synthesis/${id}`)
  },

  // 자유 비디오 생성 요청
  generateFreeVideoSynthesis: async (
    params: FreeVideoSynthesisParams,
  ): Promise<FreeVideoSynthesisResponse> => {
    return api.post('/ai-studio/free-synthesis/video', params)
  },

  // 내 결과물 목록 조회 (새로운 API)
  getMyResults: async (params: {
    page?: number
    limit?: number
    requestType?: 'image' | 'video'
  }): Promise<MyResultsResponse> => {
    return api.get('/ai-studio/free-synthesis/my-results', {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        ...(params.requestType && { requestType: params.requestType }),
      },
    })
  },
}

export interface VideoQuotaResponse {
  freeVideoQuota: number
  usedFreeVideos: number
  remainingFreeVideos: number
}

export interface AvatarType {
  id: number
  name: string
  description: string
  thumbnailUrl: string
  isActive: number
  sortOrder: number
  gender: string
  ethnicity: string
}

/// 착샷 합성
// 의류 정보 업데이트 API 파라미터
export interface UpdateGarmentInfoParams {
  editedGarmentInfo: ParsedGarmentInfo
}

export interface GarmentSynthesisRequestParams {
  avatarId: number
  selectedThemeIds: number[]
  clothesType: 'top' | 'bottom' | 'dress'
  clothesDescriptions: {
    top?: string
    bottom?: string
  }
  combinedImageUrls: { url: string; sampleImageId: number | null }[]
  uploadedImageIds: { imageId: number; garmentType: string }[]
  selectedSampleImageIds: number[]
  garmentPromptTemplate?: ''
  aspectRatio: string
}
// Synthesis Request 인터페이스들
export interface SynthesisRequestParams {
  avatarId: number
  selectedThemeIds: number[]
  selectedSampleImageIds: number[]
  uploadedImageIds: { imageId: number; garmentType: string }[]
  clothesType: 'top' | 'bottom' | 'dress'
  clothesDescriptions: {
    top?: string
    bottom?: string
  }
  combinedImageUrls: any[]
}

export interface SynthesisRequestResponse {
  id: number
  userId: number
  avatarId: number
  uploadedImageIds: { imageId: number; garmentType: string }[]
  clothesType: string
  clothesDescriptions: Record<string, string>
  combinedImageUrl: string
  totalPointCost: number
  status: string
  totalJobs: number
  completedJobs: number
  failedJobs: number
  processingJobs: number
  progressPercentage: number
  createdAt: string
  jobsCreatedAt: string
  processingStartedAt: string
  completedAt: string
  errorMessage: string
}

export interface SynthesisStatusResponse {
  id: number
  status: 'queued' | 'processing' | 'completed' | 'failed'
  progress: number
  totalJobs: number
  completedJobs: number
  failedJobs: number
  processingJobs: number
  results: SynthesisStatusResultsItem[]
}

export interface GarmentJob {
  id: number
  userRequestId: number
  avatarId: number
  combinedImageUrl: string
  garmentPrompt: string
  gcpJobId: string
  resultImageUrl: string
  isSelected: number
  variationIndex: number
  status: string
  errorMessage: string
  sampleImageId: number
}
export interface GamrentSynthesisStatusResponse {
  id: number
  userId: number
  avatarId: number
  selectedGarmentJobId: null | number
  clothesType: string
  clothesDescriptions: {
    top?: string
    bottom?: string
  }
  garmentPromptTemplate: string
  status: string
  garmentJobsCount: number
  garmentCompletedCount: number
  garmentFailedCount: number
  garmentJobs: GarmentJob[]
}

export interface SynthesisStatusResultsItem {
  jobId: number
  backgroundName: string
  poseName: string
  status: 'queued' | 'processing' | 'completed' | 'failed'
  resultUrl: string
}

// export interface SynthesisStatusResponse {
//   requestId: number
//   status: 'queued' | 'processing' | 'completed' | 'failed'
//   progressPercentage: number
//   totalJobs: number
//   completedJobs: number
//   failedJobs: number
//   processingJobs: number
//   resultUrls: string[]
//   errorMessage: string
//   estimatedCompletionTime: string
//   jobsProgress: {
//     jobId: number
//     backgroundName: string
//     poseName: string
//     status: string
//     resultUrl: string
//     errorMessage: string
//   }[]
// }

// 비디오 생성 요청 인터페이스들
export interface VideoSynthesisRequestParams {
  synthesizedImageUrls: string[]
  videoPrompt: string
  requestMetadata?: Record<string, any>
}

export interface VideoSynthesisRequestResponse {
  id: number
  userId: number
  synthesizedImageUrls: string[]
  videoPrompt: string
  pointId: number
  totalPointCost: number
  status: 'queued' | 'processing' | 'completed' | 'failed'
  gcpJobId: string
  resultVideoUrl: string
  errorMessage: string
  createdAt: string
  processingStartedAt: string
  completedAt: string
}

export interface SampleFile {
  id: number
  name: string
  garmentType: string
  imageUrl: string
  thumbnailUrl: string
  description: string
  garmentDescription: string
  category: string
  tags: string[]
  sortOrder: number
  isActive: number
}
export interface SampleResponse {
  items: SampleFile[]
  total: number
  page: number
  limit: number
}

export interface ThemeType {
  id: number
  name: string
  code: string
  description: string
  thumbnailUrl: string
  iconUrl: string
  backgroundColor: string
  sortOrder: number
  isActive: number
  createdAt: string
  updatedAt: string
}

export interface ThemesResponse {
  themes: ThemeType[]
  total: number
}

// 자유 합성 요청 인터페이스
export interface FreeSynthesisParams {
  uploadedImageUrls: string[]
  userPrompt: string
  aspectRatio: string
  model: string
}

export interface FreeSynthesisResponse {
  id: number
  status: string
  totalJobs: number
  pointCost: number
  createdAt: string
  message: string
}

// 자유 합성 목록 조회 인터페이스
export interface SynthesisJob {
  id: number
  requestId: number
  variationIndex: number
  gcpJobId: string
  resultUrl: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  errorMessage: string
  createdAt: string
  completedAt: string
}

export interface FreeSynthesisRequest {
  id: number
  requestType: string
  userId: number
  uploadedImageUrls: string[]
  userPrompt: string
  aspectRatio: string
  imageMode: string
  resolution: string
  durationSeconds: number
  totalJobs: number
  completedJobs: number
  failedJobs: number
  processingJobs: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  errorMessage: string
  pointId: number
  pointCost: number
  progressPercentage: number
  synthesisJobs: SynthesisJob[]
  createdAt: string
  processingStartedAt: string
  completedAt: string
}

export interface FreeSynthesisListResponse {
  requests: FreeSynthesisRequest[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// 자유 비디오 생성 요청 인터페이스
export interface FreeVideoSynthesisParams {
  userPrompt: string
  uploadedImageUrls?: string[]
  imageMode?: 'first_only' | 'first_and_reference' | 'first_and_last'
  resolution: '720p' | '1080p'
  durationSeconds: number
  aspectRatio: '16:9' | '9:16' | '1:1'
}

export interface FreeVideoSynthesisResponse {
  id: number
  status: string
  totalJobs: number
  pointCost: number
  createdAt: string
  message: string
}

// 내 결과물 목록 조회 응답 인터페이스
export interface MyResultsItem {
  jobId: number
  resultUrl: string
  requestType: 'image' | 'video'
  userPrompt: string
  aspectRatio: string
  imageMode: string
  resolution: string
  durationSeconds: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: string
  completedAt: string
  requestId: number
  pointCost: number
}

export interface MyResultsResponse {
  items: MyResultsItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export const themeService = {
  //모든테마 가져오기
  // 갤러리 이미지 조회
  getAllThemes: async (): Promise<ThemesResponse> => {
    try {
      return api.get('/ai-studio/themes?activeOnly=true', {})
    } catch (error) {
      console.log('get ThemeList Error ')
      return { themes: [], total: 0 }
    }
  },
}
